/**
 * Base HTTP Client with Resilience Features
 * Implements:
 * - Retry logic with exponential backoff
 * - Circuit breaker pattern for cascading failure prevention
 * - Request metrics collection
 * - Intelligent response caching (HTTP cache-aware)
 * - Memory-safe request management
 * - Request queuing with concurrency limits
 */

import type { ApiConfig, ApiResponse } from '@atlas-forms/types-js';
import { CacheManager } from './cache.manager';
import { atlasApiConfig } from './config';
import { buildAuthHeaders, wrapRequest, convertKeysToPascalCase } from './request-transformer';
import type { CacheConfig, CacheHooks } from './cache.types';
import type { RetryStrategy } from './retry-strategy';
import {
  DEFAULT_RETRY_STRATEGY,
  isRetryableError,
  calculateBackoffDelay,
  validateRetryStrategy,
} from './retry-strategy';
import {
  CircuitBreaker,
  CircuitBreakerOpenError,
  type CircuitBreakerConfig,
  type CircuitBreakerHooks,
} from './circuit-breaker';
import {
  RequestMetricsCollector,
  globalMetricsCollector,
  type RequestMetrics,
} from './request-metrics';
import { ErrorClassifier } from './error-classifier';

/**
 * Request metadata tracking for memory management
 */
interface RequestMetadata {
  /** Unique request identifier */
  requestId: string;
  /** Request start time */
  startTime: number;
  /** Timeout configuration */
  timeout: number;
  /** AbortController for request cancellation */
  controller: AbortController;
  /** Timeout ID for cleanup */
  timeoutId: ReturnType<typeof setTimeout>;
  /** Metrics being collected for this request */
  metrics?: RequestMetrics;
}

/**
 * Extended ApiConfig with cache and resilience options
 */
export interface ApiConfigWithCache extends ApiConfig {
  /** Base URL for API endpoints (explicit) */
  baseUrl: string;

  /** Timeout in milliseconds */
  timeout?: number;

  /** Default headers for all requests */
  headers?: Record<string, string>;

  /** Custom endpoints mapping */
  customEndpoints?: Record<string, string>;

  /** Cache configuration */
  cache?: Partial<CacheConfig>;

  /** Cache lifecycle hooks */
  cacheHooks?: CacheHooks;

  /** Retry strategy for failed requests */
  retryStrategy?: Partial<RetryStrategy>;

  /** Circuit breaker configuration */
  circuitBreaker?: Partial<CircuitBreakerConfig>;

  /** Circuit breaker hooks for lifecycle events */
  circuitBreakerHooks?: CircuitBreakerHooks;

  /** Enable request metrics collection (default: true) */
  enableMetrics?: boolean;

  /** Use global metrics collector instead of client-level */
  useGlobalMetrics?: boolean;

  /** Tenant ID for X-Tenant-ID header (falls back to VITE_TENANT_ID env var) */
  tenantId?: number;

  /** Bearer token for Authorization header (falls back to VITE_API_TOKEN env var) */
  token?: string;

  /**
   * When true (default), POST/PUT/PATCH request bodies are automatically
   * PascalCase-converted and wrapped in the backend metadata envelope.
   */
  autoTransformRequests?: boolean;
}

/**
 * Base HTTP client for making API requests with proper memory management and caching
 * Features:
 * - Automatic timeout cleanup with AbortController
 * - Request tracking and cancellation
 * - Request queuing with max concurrent limit
 * - Per-request timeout override
 * - Comprehensive error handling
 * - Memory leak prevention
 * - Intelligent HTTP response caching
 * - Cache-Control header parsing
 * - ETag and Last-Modified validation
 * - Stale-while-revalidate pattern support
 * - Exponential backoff retry logic
 * - Circuit breaker for cascading failure prevention
 * - Request metrics collection
 */
export class BaseHttpClient {
  // Queue size protection: prevent unbounded memory growth
  private static readonly MAX_QUEUE_SIZE = 1000;

  protected config: ApiConfigWithCache;
  /** Map of pending requests for tracking */
  protected pendingRequests: Map<string, RequestMetadata> = new Map();
  /** Maximum concurrent requests (default 10) */
  protected maxConcurrentRequests: number = 10;
  /** Queue of pending request operations */
  protected requestQueue: Array<() => Promise<void>> = [];
  /** Count of currently executing requests */
  protected activeRequestCount: number = 0;
  /** Global request counter for unique IDs */
  protected requestCounter: number = 0;
  /** Cache manager for HTTP response caching */
  protected cacheManager: CacheManager;
  /** Retry strategy configuration */
  protected retryStrategy: RetryStrategy;
  /** Circuit breaker for cascading failure prevention */
  protected circuitBreaker: CircuitBreaker;
  /** Metrics collector for this client instance */
  protected metricsCollector: RequestMetricsCollector;
  /** Queue statistics for monitoring */
  protected queueStats = { added: 0, processed: 0, rejected: 0 };

  constructor(config: ApiConfigWithCache) {
    if (!config.baseUrl) {
      throw new Error('ApiConfig must have baseUrl');
    }
    this.config = config;

    // Initialize cache manager
    this.cacheManager = new CacheManager(
      config.cache || {},
      config.cacheHooks
    );

    // Initialize retry strategy with validation
    this.retryStrategy = {
      ...DEFAULT_RETRY_STRATEGY,
      ...config.retryStrategy,
    };
    validateRetryStrategy(this.retryStrategy);

    // Initialize circuit breaker
    this.circuitBreaker = new CircuitBreaker(
      config.circuitBreaker,
      config.circuitBreakerHooks
    );

    // Initialize metrics collector
    const useGlobalMetrics = config.useGlobalMetrics ?? false;
    this.metricsCollector = useGlobalMetrics
      ? globalMetricsCollector
      : new RequestMetricsCollector();
  }

  /**
   * Make HTTP GET request
   * GET requests are automatically cached based on Cache-Control headers
   * @param endpoint API endpoint path
   * @param options Optional timeout override in milliseconds
   * @returns Promise resolving to API response
   */
  async get<T = unknown>(
    endpoint: string,
    options?: { timeout?: number }
  ): Promise<ApiResponse<T>> {
    const url = this.buildUrl(endpoint);

    // Check cache first for GET requests
    const cached = this.cacheManager.get<T>(url);
    if (cached) {
      return {
        statusCode: cached.statusCode,
        success: cached.statusCode >= 200 && cached.statusCode < 300,
        data: cached.response as T,
        metadata: {
          timestamp: new Date().toISOString(),
          cached: true,
          cacheAge: Date.now() - cached.timestamp,
        },
      };
    }

    // Check for stale-while-revalidate
    const validation = this.cacheManager.validate<T>(url);
    if (validation.isValid && validation.data) {
      // Return stale data immediately, revalidate in background
      this.revalidateInBackground<T>(endpoint, options);
      return {
        statusCode: validation.status,
        success: true,
        data: validation.data as T,
        metadata: {
          timestamp: new Date().toISOString(),
          cached: true,
          stale: true,
          cacheAge: Date.now() - (this.cacheManager.get<T>(url)?.timestamp || Date.now()),
        },
      };
    }

    return this.request<T>(endpoint, {
      method: 'GET',
      ...options,
    });
  }

  /**
   * Make HTTP POST request
   * POST requests invalidate related cache entries
   * @param endpoint API endpoint path
   * @param data Request body data
   * @param options Optional timeout override in milliseconds
   * @returns Promise resolving to API response
   */
  async post<T = unknown>(
    endpoint: string,
    data: unknown,
    options?: { timeout?: number }
  ): Promise<ApiResponse<T>> {
    // Invalidate cache for related endpoints after POST
    this.invalidateMutationCache(endpoint);

    const autoTransform = this.config.autoTransformRequests !== false;
    const body = autoTransform ? JSON.stringify(wrapRequest(data, true)) : JSON.stringify(data);

    return this.request<T>(endpoint, {
      method: 'POST',
      body,
      ...options,
    });
  }

  /**
   * Make HTTP POST query request (flat-spread envelope, used for list/get operations)
   * Equivalent to old SDK postQuery() — wraps body with nestData=false
   */
  async postQuery<T = unknown>(
    endpoint: string,
    data: unknown,
    options?: { timeout?: number }
  ): Promise<ApiResponse<T>> {
    const autoTransform = this.config.autoTransformRequests !== false;
    const body = autoTransform ? JSON.stringify(wrapRequest(data, false)) : JSON.stringify(data);

    return this.request<T>(endpoint, {
      method: 'POST',
      body,
      ...options,
    });
  }

  /**
   * Make HTTP POST request with a pre-serialized JSON body (no auto-transform).
   * Use when the endpoint expects a custom envelope different from the default wrapRequest format.
   */
  async postRaw<T = unknown>(
    endpoint: string,
    body: string,
    options?: { timeout?: number }
  ): Promise<ApiResponse<T>> {
    this.invalidateMutationCache(endpoint);
    return this.request<T>(endpoint, { method: 'POST', body, ...options });
  }

  /**
   * Make HTTP PUT request
   * PUT requests invalidate related cache entries
   * @param endpoint API endpoint path
   * @param data Request body data
   * @param options Optional timeout override in milliseconds
   * @returns Promise resolving to API response
   */
  async put<T = unknown>(
    endpoint: string,
    data: unknown,
    options?: { timeout?: number }
  ): Promise<ApiResponse<T>> {
    // Invalidate cache for this resource and related
    this.invalidateMutationCache(endpoint);

    const autoTransform = this.config.autoTransformRequests !== false;
    const body = autoTransform ? JSON.stringify(wrapRequest(data, true)) : JSON.stringify(data);

    return this.request<T>(endpoint, {
      method: 'PUT',
      body,
      ...options,
    });
  }

  /**
   * Make HTTP PUT request with a pre-serialized JSON body (no auto-transform).
   * Use when the endpoint expects a custom envelope different from the default wrapRequest format.
   */
  protected async putRaw<T = unknown>(
    endpoint: string,
    body: string,
    options?: { timeout?: number }
  ): Promise<ApiResponse<T>> {
    this.invalidateMutationCache(endpoint);
    return this.request<T>(endpoint, { method: 'PUT', body, ...options });
  }

  /**
   * Make HTTP DELETE request
   * DELETE requests invalidate related cache entries
   * @param endpoint API endpoint path
   * @param options Optional timeout override in milliseconds
   * @returns Promise resolving to API response
   */
  async delete<T = unknown>(
    endpoint: string,
    body?: unknown,
    options?: { timeout?: number }
  ): Promise<ApiResponse<T>> {
    // Invalidate cache for this resource and related
    this.invalidateMutationCache(endpoint);

    return this.request<T>(endpoint, {
      method: 'DELETE',
      body: body !== undefined ? JSON.stringify(body) : undefined,
      ...options,
    });
  }

  /**
   * Make HTTP PATCH request
   * PATCH requests invalidate related cache entries
   * @param endpoint API endpoint path
   * @param data Request body data
   * @param options Optional timeout override in milliseconds
   * @returns Promise resolving to API response
   */
  async patch<T = unknown>(
    endpoint: string,
    data: unknown,
    options?: { timeout?: number }
  ): Promise<ApiResponse<T>> {
    // Invalidate cache for this resource and related
    this.invalidateMutationCache(endpoint);

    const autoTransform = this.config.autoTransformRequests !== false;
    const body = autoTransform ? JSON.stringify(wrapRequest(data, true)) : JSON.stringify(data);

    return this.request<T>(endpoint, {
      method: 'PATCH',
      body,
      ...options,
    });
  }

  /**
   * Set maximum number of concurrent requests
   * @param max Maximum concurrent request count (default: 10)
   */
  setMaxConcurrentRequests(max: number): void {
    if (max < 1) {
      throw new Error('maxConcurrentRequests must be at least 1');
    }
    this.maxConcurrentRequests = max;
  }

  /**
   * Set retry strategy
   * @param strategy New retry strategy configuration
   * @throws Error if strategy is invalid
   */
  setRetryStrategy(strategy: Partial<RetryStrategy>): void {
    const newStrategy = { ...this.retryStrategy, ...strategy };
    validateRetryStrategy(newStrategy);
    this.retryStrategy = newStrategy;
  }

  /**
   * Get current retry strategy
   */
  getRetryStrategy(): Readonly<RetryStrategy> {
    return Object.freeze({ ...this.retryStrategy });
  }

  /**
   * Get circuit breaker state
   */
  getCircuitBreakerState(): string {
    return this.circuitBreaker.getState();
  }

  /**
   * Get circuit breaker metrics
   */
  getCircuitBreakerMetrics() {
    return this.circuitBreaker.getMetrics();
  }

  /**
   * Reset circuit breaker to CLOSED state
   * Cancels all pending requests, clears metrics, and resets state machine
   */
  resetCircuitBreaker(): void {
    // Cancel all in-flight requests
    for (const metadata of this.pendingRequests.values()) {
      try {
        metadata.controller.abort();
      } catch (error) {
        // Ignore errors from already-completed requests
      }
    }
    this.pendingRequests.clear();

    // Clear metrics
    this.metricsCollector.reset();
    this.queueStats = { added: 0, processed: 0, rejected: 0 };

    // Reset circuit breaker state
    this.circuitBreaker.reset();

    if (typeof console !== 'undefined') {
      console.info('Circuit breaker reset successfully with all pending requests cancelled');
    }
  }

  /**
   * Get metrics collector
   * Returns global or client-level collector based on configuration
   */
  getMetricsCollector(): RequestMetricsCollector {
    return this.metricsCollector;
  }

  /**
   * Get count of pending requests
   * @returns Number of requests awaiting execution or in flight
   */
  getPendingRequestCount(): number {
    return this.pendingRequests.size;
  }

  /**
   * Get queue statistics
   * @returns Queue statistics: added, processed, rejected
   */
  getQueueStats() {
    return {
      ...this.queueStats,
      queueSize: this.requestQueue.length,
      activeRequests: this.activeRequestCount,
      pendingRequests: this.pendingRequests.size,
      maxQueueSize: BaseHttpClient.MAX_QUEUE_SIZE,
    };
  }

  /**
   * Cancel all pending requests
   * @param reason Optional cancellation reason
   */
  cancelAllRequests(reason: string = 'Client shutdown'): void {
    for (const metadata of this.pendingRequests.values()) {
      this.cleanupRequest(metadata.requestId, reason);
    }
  }

  /**
   * Clear entire HTTP response cache
   */
  clearCache(): void {
    this.cacheManager.clear();
  }

  /**
   * Invalidate cache for specific URL pattern
   * @param pattern RegExp or string pattern to match
   */
  invalidateCache(pattern: RegExp | string): void {
    this.cacheManager.invalidatePattern(pattern);
  }

  /**
   * Get cache statistics
   * @returns Cache statistics object
   */
  getCacheStatistics() {
    return this.cacheManager.getStatistics();
  }

  /**
   * Enable/disable response caching
   * @param enabled Whether to enable caching
   */
  setCacheEnabled(enabled: boolean): void {
    this.cacheManager.setEnabled(enabled);
  }

  /**
   * Set default cache TTL
   * @param ttlMs TTL in milliseconds
   */
  setCacheTtl(ttlMs: number): void {
    this.cacheManager.setDefaultTtl(ttlMs);
  }

  /**
   * Set maximum cache size
   * @param maxSize Maximum number of cached entries
   */
  setCacheMaxSize(maxSize: number): void {
    this.cacheManager.setMaxSize(maxSize);
  }

  /**
   * Core request implementation with retry, circuit breaker, and caching
   * @param endpoint API endpoint path
   * @param options Fetch options with optional timeout override
   * @returns Promise resolving to API response
   */
  protected async request<T = unknown>(
    endpoint: string,
    options: RequestInit & { timeout?: number } = {}
  ): Promise<ApiResponse<T>> {
    return this.queueRequest(async () => {
      const url = this.buildUrl(endpoint);
      const method = (options.method || 'GET').toUpperCase();
      const requestId = this.generateRequestId();

      // Start metrics collection
      const metrics = this.metricsCollector.startRequest(requestId, endpoint, method);

      try {
        // Execute request with circuit breaker and retry logic
        return await this.circuitBreaker.execute(async () => {
          return await this.executeWithRetry<T>(
            url,
            requestId,
            options,
            metrics
          );
        });
      } catch (error) {
        // Handle circuit breaker rejection
        if (error instanceof CircuitBreakerOpenError) {
          this.metricsCollector.recordCircuitBreakerRejection(
            requestId,
            'OPEN'
          );
          return {
            statusCode: 503, // Service Unavailable
            success: false,
            errorMessage: `Circuit breaker is open: ${error.message}`,
            metadata: {
              timestamp: new Date().toISOString(),
              requestId,
              circuitBreakerState: 'OPEN',
            },
          };
        }

        // Other errors (should not happen as executeWithRetry handles them)
        this.metricsCollector.completeRequest(requestId);
        return this.handleError<T>(error, requestId);
      }
    });
  }

  /**
   * Execute request with retry logic and exponential backoff
   * @param url Full URL to request
   * @param requestId Request identifier
   * @param options Fetch options
   * @param metrics Metrics object to update
   * @returns API response
   */
  private async executeWithRetry<T>(
    url: string,
    requestId: string,
    options: RequestInit & { timeout?: number },
    metrics: RequestMetrics
  ): Promise<ApiResponse<T>> {
    let lastError: Error | null = null;
    let lastResponse: ApiResponse<T> | null = null;

    for (
      let attemptNumber = 0;
      attemptNumber < this.retryStrategy.maxAttempts;
      attemptNumber++
    ) {
      // Apply backoff delay (except for first attempt)
      if (attemptNumber > 0) {
        const delayMs = calculateBackoffDelay(attemptNumber - 1, this.retryStrategy);
        if (typeof console !== 'undefined') {
          console.debug(
            `Request ${requestId} retry ${attemptNumber} after ${delayMs}ms`
          );
        }
        await this.sleep(delayMs);
        metrics.totalRetryDelayMs += delayMs;
      }

      try {
        // Execute single request attempt
        const response = await this.executeRequest<T>(
          url,
          requestId,
          options,
          attemptNumber
        );

        // Record attempt
        const statusCode = response.statusCode || 0;
        this.metricsCollector.recordAttempt(
          requestId,
          statusCode,
          Date.now() - metrics.startTime,
          response.success
        );

        // Check if response is successful or non-retryable error
        if (response.success || !isRetryableError(response.errorMessage, statusCode, this.retryStrategy)) {
          this.metricsCollector.completeRequest(requestId);
          return response;
        }

        // Response is retryable error, continue loop
        lastResponse = response;
        lastError = new Error(`HTTP ${statusCode}: ${response.errorMessage}`);

        if (attemptNumber === this.retryStrategy.maxAttempts - 1) {
          // Last attempt failed, return error
          this.metricsCollector.completeRequest(requestId);
          return response;
        }

        continue;
      } catch (error) {
        // Handle network/timeout errors
        lastError = error as Error;
        const statusCode = error instanceof Error && error.name === 'AbortError' ? 408 : 0;

        this.metricsCollector.recordAttempt(
          requestId,
          statusCode,
          Date.now() - metrics.startTime,
          false,
          (error as Error).message
        );

        // Check if error is retryable
        if (!isRetryableError(error, statusCode, this.retryStrategy)) {
          this.metricsCollector.completeRequest(requestId);
          return this.handleError<T>(error, requestId);
        }

        // Error is retryable, continue loop
        if (attemptNumber === this.retryStrategy.maxAttempts - 1) {
          this.metricsCollector.completeRequest(requestId);
          return this.handleError<T>(error, requestId);
        }

        continue;
      }
    }

    // All retries exhausted
    this.metricsCollector.completeRequest(requestId);
    if (lastResponse) {
      return lastResponse;
    }

    return this.handleError<T>(
      lastError || new Error('Unknown error after retries'),
      requestId
    );
  }

  /**
   * Execute a single request attempt
   * @param url Full URL
   * @param requestId Request identifier
   * @param options Fetch options
   * @param attemptNumber Zero-based attempt number
   * @returns API response
   */
  private async executeRequest<T>(
    url: string,
    requestId: string,
    options: RequestInit & { timeout?: number },
    _attemptNumber: number
  ): Promise<ApiResponse<T>> {
    const timeout = options.timeout ?? this.config.timeout ?? 30000;
    const method = (options.method || 'GET').toUpperCase();

    // Create abort controller and set up timeout cleanup
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, timeout);

    const metadata: RequestMetadata = {
      requestId,
      startTime: Date.now(),
      timeout,
      controller,
      timeoutId,
    };

    // Track request for memory management
    this.pendingRequests.set(requestId, metadata);

    try {
      // Read auth headers dynamically from atlasApiConfig (not from stale this.config)
      // This ensures the token is always current even for singleton client instances
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...buildAuthHeaders(atlasApiConfig.tenantId, atlasApiConfig.token),
        ...(this.config.headers || {}),
        ...(options.headers as Record<string, string> || {}),
      };

      // Add conditional request headers for GET if cache validation needed
      if (method === 'GET') {
        const validation = this.cacheManager.validate<T>(url);
        if (validation.etag) {
          headers['If-None-Match'] = validation.etag;
        }
        if (validation.lastModified) {
          headers['If-Modified-Since'] = validation.lastModified;
        }
      }

      const response = await fetch(url, {
        ...options,
        headers,
        signal: controller.signal,
      });

      // Handle 304 Not Modified
      if (response.status === 304 && method === 'GET') {
        const cached = this.cacheManager.handle304(url);
        if (cached) {
          return {
            statusCode: 200,
            success: true,
            data: cached.response as T,
            metadata: {
              timestamp: new Date().toISOString(),
              cached: true,
              notModified: true,
            },
          };
        }
      }

      const result = await this.handleResponse<T>(response);

      // Cache GET responses only
      if (method === 'GET' && result.success) {
        const responseHeaders = this.extractHeaders(response);
        this.cacheManager.set(url, result.data, undefined, responseHeaders, response.status);
      }

      return result;
    } catch (error) {
      return this.handleError<T>(error, requestId);
    } finally {
      // Always clean up the request metadata and timeout
      this.cleanupRequest(requestId);
    }
  }

  /**
   * Queue request to enforce max concurrent limit
   * Implements backpressure when queue is full
   * @param requestFn Function that executes the request
   * @returns Promise resolving to API response
   */
  protected async queueRequest<T>(
    requestFn: () => Promise<ApiResponse<T>>
  ): Promise<ApiResponse<T>> {
    // Check queue size before adding
    if (this.requestQueue.length >= BaseHttpClient.MAX_QUEUE_SIZE) {
      this.queueStats.rejected++;
      return {
        statusCode: 503,
        success: false,
        errorMessage: `Request queue full (${this.requestQueue.length}/${BaseHttpClient.MAX_QUEUE_SIZE}). Too many pending requests. Try again later.`,
        metadata: {
          timestamp: new Date().toISOString(),
          queueStats: this.getQueueStats(),
        },
      };
    }

    return new Promise((resolve) => {
      const execute = async () => {
        try {
          const result = await requestFn();
          this.queueStats.processed++;
          resolve(result);
        } finally {
          this.activeRequestCount--;
          this.processQueue();
        }
      };

      if (this.activeRequestCount < this.maxConcurrentRequests) {
        this.activeRequestCount++;
        this.queueStats.added++;
        execute();
      } else {
        this.queueStats.added++;
        this.requestQueue.push(execute);
      }
    });
  }

  /**
   * Process queued requests when slot becomes available
   */
  protected processQueue(): void {
    if (this.requestQueue.length === 0) {
      return;
    }

    if (this.activeRequestCount < this.maxConcurrentRequests) {
      const execute = this.requestQueue.shift();
      if (execute) {
        this.activeRequestCount++;
        execute();
      }
    }
  }

  /**
   * Generate unique request identifier
   * @returns Unique request ID
   */
  protected generateRequestId(): string {
    return `req_${Date.now()}_${++this.requestCounter}`;
  }

  /**
   * Clean up request metadata and timeout
   * @param requestId Request identifier
   * @param reason Optional cleanup reason (for logging)
   */
  protected cleanupRequest(requestId: string, reason?: string): void {
    const metadata = this.pendingRequests.get(requestId);
    if (metadata) {
      // Clear the timeout to prevent memory leak
      clearTimeout(metadata.timeoutId);
      this.pendingRequests.delete(requestId);

      // Log cleanup if reason provided (for debugging)
      if (reason && typeof console !== 'undefined') {
        const duration = Date.now() - metadata.startTime;
        console.debug(
          `Request ${requestId} cleaned up: ${reason} (duration: ${duration}ms)`
        );
      }
    }
  }

  /**
   * Build full URL from endpoint
   * @param endpoint Endpoint path
   * @returns Complete URL
   */
  protected buildUrl(endpoint: string): string {
    const base = this.config.baseUrl.replace(/\/$/, '');
    const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    return `${base}${path}`;
  }

  /**
   * Handle successful response
   * @param response Fetch response object
   * @returns Formatted API response
   */
  protected async handleResponse<T>(
    response: Response
  ): Promise<ApiResponse<T>> {
    const contentType = response.headers.get('content-type');

    let data: unknown;
    try {
      if (contentType?.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
      }
    } catch (parseError) {
      const errorMsg = parseError instanceof Error ? parseError.message : 'Failed to parse response';
      return {
        statusCode: response.status,
        success: false,
        errorMessage: `Response parse error: ${errorMsg}`,
        metadata: {
          timestamp: new Date().toISOString(),
        },
      };
    }

    if (!response.ok) {
      return {
        statusCode: response.status,
        success: false,
        errorMessage: this.formatErrorResponseMessage(data, response.status),
        metadata: { timestamp: new Date().toISOString() },
      };
    }

    // Unwrap BizFirst envelope: convert keys to PascalCase then extract Data
    const converted = convertKeysToPascalCase(data) as Record<string, unknown>;

    if (converted && typeof converted === 'object') {
      // Envelope: { Success, Data, Errors } or { Success, Data, PageInfo } (list endpoints)
      if ('Success' in converted && 'Errors' in converted) {
        if (converted['Success'] === false) {
          const errors = converted['Errors'] as Record<string, unknown>[];
          const firstError = Array.isArray(errors) && errors.length > 0 ? errors[0] : null;
          return {
            statusCode: response.status,
            success: false,
            errorMessage: (firstError?.['Message'] as string) ?? 'An error occurred',
            metadata: { timestamp: new Date().toISOString() },
          };
        }
        // If PageInfo exists (list endpoints), return full object { Data, PageInfo }
        if ('PageInfo' in converted) {
          return {
            statusCode: response.status,
            success: true,
            data: { Data: converted['Data'], PageInfo: converted['PageInfo'] } as T,
            metadata: { timestamp: new Date().toISOString() },
          };
        }
        return {
          statusCode: response.status,
          success: true,
          data: converted['Data'] as T,
          metadata: { timestamp: new Date().toISOString() },
        };
      }

      // Nested envelope: { Data: { Success, Data, Errors } } or { Data: [...] }
      if ('Data' in converted) {
        const inner = converted['Data'];
        if (Array.isArray(inner)) {
          // If PageInfo exists (list endpoints), return full object { Data, PageInfo }
          if ('PageInfo' in converted) {
            return { statusCode: response.status, success: true, data: { Data: inner, PageInfo: converted['PageInfo'] } as T, metadata: { timestamp: new Date().toISOString() } };
          }
          return { statusCode: response.status, success: true, data: inner as T, metadata: { timestamp: new Date().toISOString() } };
        }
        if (inner && typeof inner === 'object') {
          const innerObj = inner as Record<string, unknown>;
          if ('Success' in innerObj && 'Errors' in innerObj) {
            if (innerObj['Success'] === false) {
              const errors = innerObj['Errors'] as Record<string, unknown>[];
              const firstError = Array.isArray(errors) && errors.length > 0 ? errors[0] : null;
              return {
                statusCode: response.status,
                success: false,
                errorMessage: (firstError?.['Message'] as string) ?? 'An error occurred',
                metadata: { timestamp: new Date().toISOString() },
              };
            }
            return { statusCode: response.status, success: true, data: innerObj['Data'] as T, metadata: { timestamp: new Date().toISOString() } };
          }
        }
        // If PageInfo exists at root level (list endpoints), return full object
        if ('PageInfo' in converted) {
          return { statusCode: response.status, success: true, data: { Data: inner, PageInfo: converted['PageInfo'] } as T, metadata: { timestamp: new Date().toISOString() } };
        }
        return { statusCode: response.status, success: true, data: inner as T, metadata: { timestamp: new Date().toISOString() } };
      }
    }

    return {
      statusCode: response.status,
      success: true,
      data: converted as T,
      metadata: { timestamp: new Date().toISOString() },
    };
  }

  /**
   * Build a safe, human-readable error message for a non-2xx HTTP response.
   *
   * Never surfaces the raw response body to the UI. For auth failures (401/403)
   * the server's Problem+JSON body is intentionally ignored in favor of a
   * consistent, friendly "please sign in again" message, since the body for
   * those responses is typically just RFC 9110/7807 metadata (type/title/status/
   * traceId) and not something a user should ever see rendered as page content.
   * For other statuses, a server-provided detail/message is used when present
   * (useful for validation errors), otherwise falls back to a generic,
   * status-appropriate message from {@link ErrorClassifier}.
   *
   * @param data Parsed response body (JSON object, or raw text for non-JSON responses)
   * @param status HTTP status code
   */
  protected formatErrorResponseMessage(data: unknown, status: number): string {
    const classified = ErrorClassifier.classify(undefined, status);

    // Auth failures: always use the consistent, user-friendly message.
    if (status === 401 || status === 403) {
      return classified.technicalMessage;
    }

    // Prefer a server-supplied human message when the body provides one
    // (e.g. validation errors), but never fall back to dumping the raw body.
    if (data && typeof data === 'object') {
      const obj = data as Record<string, unknown>;
      const candidate =
        (obj.detail as string | undefined) ??
        (obj.Detail as string | undefined) ??
        (obj.message as string | undefined) ??
        (obj.Message as string | undefined) ??
        (obj.title as string | undefined) ??
        (obj.Title as string | undefined);
      if (typeof candidate === 'string' && candidate.trim().length > 0) {
        return candidate;
      }
    } else if (typeof data === 'string') {
      const trimmed = data.trim();
      // Plain-text bodies are fine to show; JSON-looking text is not
      // (it means we failed to detect/parse JSON upstream).
      if (trimmed.length > 0 && !trimmed.startsWith('{') && !trimmed.startsWith('[')) {
        return trimmed;
      }
    }

    return classified.technicalMessage;
  }

  /**
   * Handle request error with detailed error classification and recovery suggestions
   * @param error Error object
   * @param requestId Request identifier for logging
   * @returns Formatted error API response with actionable details
   */
  protected handleError<T>(error: unknown, requestId?: string): ApiResponse<T> {
    let message = 'Request failed';
    let details = 'An unexpected error occurred while processing the request';
    let suggestion = 'Try again, or contact support if the problem persists';
    let statusCode = 0;
    let errorCode = 'HTTP_ERROR';

    if (error instanceof Error) {
      // Classify error type and provide actionable messages
      if (error.name === 'AbortError') {
        message = 'Request timeout';
        details = 'The server took too long to respond (timeout >30s)';
        suggestion = 'Check your internet connection and try again';
        statusCode = 408;
        errorCode = 'TIMEOUT_ERROR';
      } else if (error instanceof TypeError) {
        if (error.message.includes('fetch')) {
          message = 'Network connection failed';
          details = 'Unable to reach the server. Check your internet connection.';
          suggestion = 'Verify your network is connected and try again';
        } else {
          message = 'Request processing error';
          details = error.message;
          suggestion = 'Refresh the page and try again';
        }
        statusCode = 0;
        errorCode = 'NETWORK_ERROR';
      } else {
        message = error.message;
        details = error.message;
      }
    }

    const errorMetadata: Record<string, unknown> = {
      timestamp: new Date().toISOString(),
      requestId,
      errorCode,
      statusCode,
      details,
      suggestion,
      recoveryActions: this.getRecoveryActions(statusCode, errorCode),
    };

    if (requestId && typeof console !== 'undefined') {
      console.error(
        `Request ${requestId} failed (${errorCode}): ${message}\nDetails: ${details}\nSuggestion: ${suggestion}`
      );
    }

    return {
      statusCode,
      success: false,
      errorMessage: message,
      metadata: errorMetadata,
    };
  }

  /**
   * Get recovery actions for specific error types
   * Provides consistent, actionable recovery suggestions for all error codes
   *
   * @example
   * // For timeout errors
   * const actions = client.getRecoveryActions(408, 'TIMEOUT_ERROR');
   * // Returns: ['Check your internet connection', 'Try the request again', ...]
   *
   * @example
   * // For authentication errors
   * const actions = client.getRecoveryActions(401, 'AUTH_ERROR');
   * // Returns: ['Log in again', 'Check your session has not expired']
   *
   * @example
   * // For server errors with default actions
   * const actions = client.getRecoveryActions(503, 'SERVER_ERROR');
   * // Returns: ['Try again in a few moments', 'Contact support...']
   *
   * @param statusCode HTTP status code
   * @param errorCode Error classification code
   * @returns Array of suggested recovery actions (always has at least one action)
   */
  protected getRecoveryActions(statusCode: number, errorCode: string): string[] {
    const actions: string[] = [];

    // Classify errors and provide targeted recovery actions
    if (errorCode === 'TIMEOUT_ERROR') {
      actions.push('Check your internet connection');
      actions.push('Try the request again');
      actions.push('Increase timeout limit if the operation requires more time');
      actions.push('Check if the server is experiencing high load');
    } else if (errorCode === 'NETWORK_ERROR') {
      actions.push('Verify your network connection is active');
      actions.push('Try refreshing the page or application');
      actions.push('Check if the server is available and accessible');
      actions.push('Wait a moment and try again');
    } else if (statusCode === 400) {
      actions.push('Check the request parameters');
      actions.push('Verify all required fields are provided');
      actions.push('Review the API documentation for correct format');
    } else if (statusCode === 401) {
      actions.push('Log in again with valid credentials');
      actions.push('Check your session has not expired');
      actions.push('Verify your authentication token is valid');
    } else if (statusCode === 403) {
      actions.push('Check your access permissions for this resource');
      actions.push('Contact support for assistance');
      actions.push('Request access from administrator if needed');
    } else if (statusCode === 404) {
      actions.push('Verify the resource ID or path is correct');
      actions.push('Refresh the page to get current data');
      actions.push('Check if the resource has been deleted or moved');
    } else if (statusCode === 409) {
      actions.push('Check if the resource has been modified by another user');
      actions.push('Refresh and try your operation again');
      actions.push('Resolve the conflict before retrying');
    } else if (statusCode === 429) {
      actions.push('Wait before making another request (rate limit exceeded)');
      actions.push('Reduce the frequency of requests');
      actions.push('Check rate limit headers for retry-after timing');
    } else if (statusCode >= 500) {
      actions.push('Try again in a few moments');
      actions.push('Check the service status page');
      actions.push('Contact support if the problem persists');
    } else if (statusCode >= 400) {
      // Default for other 4xx errors
      actions.push('Review the request for errors');
      actions.push('Check the API documentation');
      actions.push('Contact support if the issue persists');
    } else if (statusCode === 0 && errorCode === 'HTTP_ERROR') {
      // Unknown error with no status code
      actions.push('Check your internet connection');
      actions.push('Try refreshing the page');
      actions.push('Contact support with the error details');
    } else {
      // Fallback for any uncovered status codes
      actions.push('Try the request again');
      actions.push('Contact support if the problem persists');
    }

    return actions.length > 0 ? actions : ['Contact support for assistance'];
  }

  /**
   * Resolve endpoint URL from config
   * @param key Endpoint configuration key
   * @returns Resolved endpoint string
   */
  protected resolveEndpoint(key: string): string {
    const endpoint =
      this.config.customEndpoints?.[key] ||
      this.getConfigValue(key);

    if (typeof endpoint !== 'string') {
      throw new Error(`Endpoint not configured: ${key}`);
    }

    return endpoint;
  }

  /**
   * Get typed config value by key
   * @param key Configuration key
   * @returns Configuration value or undefined
   */
  private getConfigValue(key: string): string | undefined {
    const value = (this.config as unknown as Record<string, unknown>)[key];
    return typeof value === 'string' ? value : undefined;
  }

  /**
   * Build query URL with parameters
   * @param endpoint Base endpoint
   * @param params Query parameters object
   * @returns URL with encoded query string
   */
  protected buildQueryUrl(
    endpoint: string,
    params?: Record<string, unknown>
  ): string {
    if (!params) {
      return endpoint;
    }

    const searchParams = new URLSearchParams();
    const entries = Object.entries(params);

    for (const [key, value] of entries) {
      if (value !== null && value !== undefined) {
        searchParams.append(key, String(value));
      }
    }

    const queryString = searchParams.toString();
    return queryString ? `${endpoint}?${queryString}` : endpoint;
  }

  /**
   * Sleep for specified milliseconds
   * @param ms Milliseconds to sleep
   */
  protected sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Extract headers from fetch Response
   * @param response Fetch response object
   * @returns Headers as key-value object
   */
  protected extractHeaders(response: Response): Record<string, string> {
    const headers: Record<string, string> = {};
    response.headers.forEach((value, key) => {
      headers[key.toLowerCase()] = value;
    });
    return headers;
  }

  /**
   * Invalidate cache for mutations (POST, PUT, DELETE, PATCH)
   * Invalidates the direct endpoint and list endpoints
   * @param endpoint Modified endpoint
   */
  protected invalidateMutationCache(endpoint: string): void {
    // Extract resource type from endpoint for pattern matching
    const match = endpoint.match(/^\/([a-z]+)/i);
    if (match) {
      const resource = match[1];
      // Invalidate:
      // 1. The direct endpoint (e.g., /forms/123)
      // 2. List endpoints (e.g., /forms, /forms/list)
      // 3. Search endpoints (e.g., /forms/search)
      this.invalidateCache(new RegExp(`^\/${resource}($|\/|\\?)`));
    }
  }

  /**
   * Revalidate cache entry in background
   * Used for stale-while-revalidate pattern
   * Implements proper error handling and timeout management to prevent memory leaks
   * @param endpoint API endpoint path
   * @param options Request options
   */
  protected async revalidateInBackground<T = unknown>(
    endpoint: string,
    options?: { timeout?: number }
  ): Promise<void> {
    const abortController = new AbortController();
    let revalidationTimeout: ReturnType<typeof setTimeout> | null = null;

    try {
      const url = this.buildUrl(endpoint);
      const validation = this.cacheManager.validate<T>(url);

      if (validation.needsConditionalRequest) {
        // Set 30-second timeout for background revalidation
        // This prevents orphaned requests from running indefinitely
        revalidationTimeout = setTimeout(() => {
          abortController.abort();
          this.metricsCollector.recordRevalidationTimeout(endpoint);
        }, options?.timeout ?? 30000);

        // Make background request with proper error handling
        // Both .then() and .catch() must be attached to prevent unhandled rejections
        this.request<T>(endpoint, {
          method: 'GET',
          ...options,
          signal: abortController.signal,
        })
          .then(() => {
            // Success: record metrics
            this.metricsCollector.recordRevalidationSuccess(endpoint);
            if (revalidationTimeout !== null) clearTimeout(revalidationTimeout);
          })
          .catch((error: Error) => {
            // Always record metrics regardless of outcome
            this.metricsCollector.recordRevalidationFailure(endpoint, error);
            if (revalidationTimeout !== null) clearTimeout(revalidationTimeout);

            // Log debug info (respects config.cache?.debug flag)
            if (typeof console !== 'undefined') {
              // Distinguish between abort and actual errors
              if (error.name === 'AbortError') {
                if (this.config.cache?.debug) {
                  console.debug(`Background revalidation timeout for ${endpoint}`);
                }
              } else {
                if (this.config.cache?.debug) {
                  console.debug(`Background revalidation failed for ${endpoint}:`, error.message);
                }
              }
            }
          });
      }
    } catch (error) {
      // Silently ignore errors during initial validation setup
      // This prevents cascade failures from breaking the request flow
      if (revalidationTimeout !== null) clearTimeout(revalidationTimeout);
      this.metricsCollector.recordRevalidationFailure(endpoint, error instanceof Error ? error : new Error(String(error)));
    }
  }
}
